import { useRef, useState } from "react";
import { Alert, Box, Button, ListItem, Stack, Typography } from "@mui/material";
import {
  getPaymentAuthenticationRequired,
  updatePaymentAuthenticationForError,
} from "@bw/shared/api";
import type { CoachingPayment } from "@bw/shared/api";
import { formatMoney } from "@bw/shared/utils";
import {
  useCoachingPayment,
  useFundCoachingProposal,
  useReconcileCoachingPayment,
} from "@bw/shared/hooks";
import { coachingPaymentPrice } from "./paymentPrice";
import { CoachingPaymentPanel } from "./CoachingPaymentPanel";
import { useLessonFrontendConfig } from "../hooks/usePayment";
import {
  MaterialConnectedElementsProvider,
  MaterialPaymentIntentAuthentication,
} from "../../../common/components/payment";

export function CoachingPaymentRow({
  payment,
  canAct,
}: {
  payment: CoachingPayment;
  canAct: () => boolean;
}) {
  const price = coachingPaymentPrice(payment);
  const reschedule = payment.operation === "reschedule";
  const disclosedPrice = price !== null;
  const [payOpen, setPayOpen] = useState(false);
  const reviewedPayment = useCoachingPayment(payment.acceptance_id, payOpen);
  const [authentication, setAuthentication] = useState<{
    clientSecret: string;
  } | null>(null);
  const reconcile = useReconcileCoachingPayment();
  const fund = useFundCoachingProposal();
  const freeKey = useRef<string | null>(null);
  const busy = useRef(false);
  const config = useLessonFrontendConfig(
    payment.facility_id,
    authentication !== null,
  );
  async function checkPayment() {
    if (!canAct() || busy.current) return;
    busy.current = true;
    try {
      await reconcile.mutateAsync([payment.acceptance_id]);
      setAuthentication(null);
    } catch (error) {
      const required = getPaymentAuthenticationRequired(error);
      if (required) setAuthentication({ clientSecret: required.clientSecret });
    } finally {
      busy.current = false;
    }
  }
  return (
    <ListItem divider sx={{ display: "block", p: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ justifyContent: "space-between" }}
      >
        <Stack>
          <Typography sx={{ fontWeight: 700 }}>
            {reschedule ? "Coaching reschedule · No additional payment" : `Coaching payment · ${price ?? "—"}`}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {reschedule && payment.status === "not_started" ? "Ready to apply" : payment.status.replaceAll("_", " ")}
          </Typography>
          {payment.campaign ? (
            <Typography color="text.secondary" variant="body2">
              {payment.campaign.name} · Save {formatMoney(
                payment.campaign.discount,
                payment.currency,
              )}
            </Typography>
          ) : null}
        </Stack>
        <Stack direction="row" spacing={1}>
          {payment.can_fund && disclosedPrice && !payOpen ? (
            <Button
              size="small"
              variant="contained"
              disabled={fund.isPending}
              onClick={() => {
                if (!canAct() || busy.current) return;
                if (payment.status !== "free" && !reschedule) {
                  setPayOpen(true);
                  return;
                }
                busy.current = true;
                freeKey.current ??= crypto.randomUUID();
                fund.mutate(
                  [
                    payment.acceptance_id,
                    {
                      revision_id: payment.revision_id,
                      payment_method_id: null,
                      idempotency_key: freeKey.current,
                      campaign_id: null,
                      skip_promotions: false,
                    },
                  ],
                  {
                    onSettled: () => {
                      busy.current = false;
                    },
                  },
                );
              }}
            >
              {reschedule ? "Apply reschedule" : payment.status === "free" ? "Confirm free lesson" : "Pay"}
            </Button>
          ) : null}
          {payment.can_recover && !authentication ? (
            <Button
              size="small"
              disabled={reconcile.isPending}
              onClick={() => void checkPayment()}
            >
              Check payment
            </Button>
          ) : null}
        </Stack>
      </Stack>
      {payment.can_fund && !disclosedPrice ? (
        <Typography variant="body2" color="text.secondary">
          Price unavailable — contact the coach.
        </Typography>
      ) : null}
      {payOpen && !reschedule ? (
        <Box
          hidden={!payment.can_fund && !payment.can_recover}
          inert={!payment.can_fund && !payment.can_recover}
        >
          {reviewedPayment.isPending ? (
            <Typography role="status">Reviewing the current total…</Typography>
          ) : reviewedPayment.isError || !reviewedPayment.data ? (
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void reviewedPayment.refetch()}
                >
                  Retry
                </Button>
              }
            >
              The current coaching total is unavailable. No payment was made.
            </Alert>
          ) : (
            <CoachingPaymentPanel
              key={`${reviewedPayment.data.acceptance_id}:${reviewedPayment.data.revision_id}`}
              payment={reviewedPayment.data}
              canAct={canAct}
            />
          )}
        </Box>
      ) : null}
      {authentication ? (
        config.data ? (
          <MaterialConnectedElementsProvider config={config.data}>
            <MaterialPaymentIntentAuthentication
              clientSecret={authentication.clientSecret}
              onAuthenticated={async (secret) => {
                if (secret !== authentication.clientSecret) return;
                try {
                  await reconcile.mutateAsync([payment.acceptance_id]);
                  setAuthentication(null);
                } catch (error) {
                  setAuthentication((current) =>
                    updatePaymentAuthenticationForError(current, secret, error),
                  );
                  throw error;
                }
              }}
            />
          </MaterialConnectedElementsProvider>
        ) : config.isError ? (
          <Alert severity="error">
            Payment authentication is unavailable. Please try again.
          </Alert>
        ) : (
          <Typography role="status">Loading payment authentication…</Typography>
        )
      ) : null}
      {(reconcile.isError && !authentication) || fund.isError ? (
        <Alert severity="error">
          {reschedule ? "The reschedule could not be applied. Refresh and try again." : "The payment could not be confirmed. Please try again."}
        </Alert>
      ) : null}
    </ListItem>
  );
}
